import { type App } from "vue"
import * as Sentry from "@sentry/vue"
// Importation des styles globaux
import "./style.css"
import "primeicons/primeicons.css"

// Thrird party
import * as yup from "yup"
// Store
import { createPinia } from "pinia"
import { useAuthStore } from "@core/stores/auth.store"
// Plugins
import servicePlugin from "@core/plugins/services.plugin"
import fontAwesomePlugin from "@core/plugins/fontawesome.plugin"
import routerPlugin from "@core/plugins/router.plugin"
import trackingPlugin from "@core/plugins/tracking.plugin"
import sidebarRegistryPlugin from "@core/plugins/sidebar-registry.plugin"
import i18nPlugin from "@core/plugins/i18n.plugin"

// PrimeVue
import { loadPrimeVue } from "./primevue"
import pusherPlugin from "@core/plugins/pusher.plugin"
import { loadModules } from "./loadModules"

// ================================
// Embed : routes sans attente du token parent (postMessage)
// ================================

/**
 * En iframe (`embed=1`), le chargement par défaut attend un `postMessage` `update:token`
 * depuis l’app parente. Certaines routes gèrent l’auth autrement : on monte l’app tout de suite.
 *
 * - **Inscription** : parcours public.
 * - **`/demande/*`** : lien e-mail ; le JWT invité est souvent en query (`?token=`) dans l’URL de l’iframe.
 */
function isEmbedRouteWithoutParentToken(pathname: string): boolean {
    if (!pathname) return false
    const normalized = pathname.replace(/\/$/, "") || "/"
    if (normalized === "/inscription" || normalized === "inscription") {
        return true
    }
    return normalized.includes("/demande/")
}

export function loadApp(
    app: App,
    { mount = true }: { mount: boolean } = {
        mount: true,
    }
) {
    // Détection du mode Storybook
    const isStorybook = import.meta.env.STORYBOOK === "true"
    const isDev = import.meta.env.DEV

    // Chargement des plugins de base
    loadPlugins(app, { isStorybook })

    // Chargement des traductions Yup
    loadYupTranslation()

    // Chargement des composants PrimeVue
    loadPrimeVue(app)

    // Chargement de sentry (désactivé en mode Storybook)
    if (!isStorybook) {
        loadSentry(app)
    }

    // Variable pour tracker si le router a déjà été installé
    let routerInstalled = false

    /**
     * Fonction helper pour installer le router (et le tracking V0 qui en dépend) de manière sécurisée.
     *
     * Idempotence durcie : `routerInstalled` est local à cet appel de `loadApp`,
     * mais sous Storybook l'instance `app` (et son `$route`) peut être réutilisée
     * entre rendus / HMR. Ré-appeler `app.use(routerPlugin)` sur une app qui a
     * déjà `$route` plante (`Cannot redefine property: $route`, défini
     * non-configurable par vue-router). On s'appuie donc aussi sur
     * `globalProperties`, qui survit aux rendus. En prod (app fraîche), `$route`
     * est absent → comportement inchangé.
     */
    const installRouter = () => {
        if (routerInstalled || "$route" in app.config.globalProperties) {
            routerInstalled = true
            return
        }
        app.use(routerPlugin)
        // Tracking V0 : registre l'afterEach hook → `Page Viewed` events.
        // Doit être après routerPlugin (le router doit exister).
        // Désactivé en mode Storybook (pas de tracking dans les stories).
        if (!isStorybook) {
            app.use(trackingPlugin)
        }
        routerInstalled = true
    }

    // En mode Storybook, installer le router immédiatement pour que useRoute() fonctionne
    if (isStorybook) {
        installRouter()
    }

    // Chargement des modules (avec leurs hooks d'initialisation)
    loadModules(app, { isStorybook, isDev }).then(async () => {
        const authStore = useAuthStore()
        const isEmbed =
            typeof window !== "undefined" &&
            new URLSearchParams(window.location.search).get("embed") === "1"
        const hasRestoredSession = !isEmbed && authStore.hydrateFromStorage()
        const pathname =
            typeof window !== "undefined" ? window.location.pathname : ""
        const mountEmbedWithoutWaitingParentToken =
            isEmbed && isEmbedRouteWithoutParentToken(pathname)

        const mountAppWithRouter = () => {
            installRouter()
            if (mount) {
                app.mount("#app")
            }
        }

        if (import.meta.env.DEV) {
            if (hasRestoredSession) {
                // Session restaurée depuis le localStorage
                if (!isStorybook) {
                    app.use(routerPlugin)
                }
                if (mount) app.mount("#app")
                return
            }
            // Même logique qu’en prod : en iframe, attendre le token parent sauf routes dédiées
            if (mountEmbedWithoutWaitingParentToken) {
                mountAppWithRouter()
                return
            }
            if (!isEmbed) {
                if (!isStorybook) {
                    installRouter()
                }
                if (mount) {
                    app.mount("#app")
                }
                return
            }
            const tokenPromise = new Promise<{
                token: string
                user?: Record<string, unknown>
            }>((resolve) => {
                window.addEventListener("message", (event) => {
                    if (
                        event.data?.type === "update:token" &&
                        event.data?.token
                    ) {
                        resolve({
                            token: event.data.token as string,
                            user: event.data.user as
                                | Record<string, unknown>
                                | undefined,
                        })
                    }
                })
            })
            tokenPromise.then(({ token, user }) => {
                authStore.setToken(token)
                if (user) {
                    authStore.setUserFromEmbed(user)
                }
                mountAppWithRouter()
            })
        } else {
            if (hasRestoredSession) {
                installRouter()
                if (mount) {
                    app.mount("#app")
                }
                return
            }
            if (mountEmbedWithoutWaitingParentToken) {
                mountAppWithRouter()
                return
            }
            if (!isEmbed) {
                mountAppWithRouter()
                return
            }
            const tokenPromise = new Promise<{
                token: string
                user?: Record<string, unknown>
            }>((resolve) => {
                window.addEventListener("message", (event) => {
                    if (
                        event.data?.type === "update:token" &&
                        event.data?.token
                    ) {
                        resolve({
                            token: event.data.token as string,
                            user: event.data.user as
                                | Record<string, unknown>
                                | undefined,
                        })
                    }
                })
            })
            tokenPromise.then(({ token, user }) => {
                authStore.setToken(token)
                if (user) {
                    authStore.setUserFromEmbed(user)
                }
                mountAppWithRouter()
            })
        }
    })
}

/**
 * Charge les plugins globaux
 * @param app
 * @param options - Options de chargement
 * @param options.isStorybook - Mode Storybook (désactive certains plugins)
 */
function loadPlugins(
    app: App,
    { isStorybook = false }: { isStorybook?: boolean } = {}
) {
    // Pinia
    app.use(createPinia())
    // Fontawesome
    app.use(fontAwesomePlugin)
    // Les services
    app.use(servicePlugin)
    // Registry des sidebars (building, source, user)
    app.use(sidebarRegistryPlugin)

    // Pusher (désactivé en mode Storybook ou si la clé n'est pas définie)
    if (!isStorybook && import.meta.env.VITE_PUSHER_KEY) {
        app.use(pusherPlugin, {
            key: import.meta.env.VITE_PUSHER_KEY,
        })
    }

    // I18n
    loadI18n(app)
}

function loadI18n(app: App) {
    // I18n
    yup.setLocale({
        mixed: {
            required: "Ce champ est obligatoire",
        },
        string: {
            email: "Veuillez saisir une adresse e-mail valide",
        },
    })
    app.use(i18nPlugin)
}

/**
 * Charge les traductions Yup
 */
function loadYupTranslation() {
    yup.setLocale({
        mixed: {
            required: "Ce champ est obligatoire",
        },
        string: {
            email: "Veuillez saisir une adresse e-mail valide",
            min: "Veuillez saisir au moins ${min} caractères",
            max: "Veuillez saisir au plus ${max} caractères",
            matches: "Le format n'est pas correcte.",
        },
    })
}

/**
 * Chargement de Sentry
 * @param app - Instance de l'application Vue
 */
function loadSentry(app: App) {
    const dsn =
        import.meta.env.VITE_SENTRY_DSN ??
        "https://8effbab4c9e67953c9871f2b52eccd74@o342045.ingest.us.sentry.io/4508008737079296"
    const environment =
        import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE
    const release = import.meta.env.VITE_SENTRY_RELEASE

    Sentry.init({
        app,
        dsn,
        environment,
        release,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true,
            }),
        ],
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.05,
        replaysOnErrorSampleRate: 1.0,
        beforeSend(event) {
            const message = event.exception?.values?.[0]?.value ?? ""
            // Bruit récurrent inoffensif renvoyé par certains navigateurs.
            if (/ResizeObserver loop/.test(message)) return null
            if (/Non-Error promise rejection captured/.test(message))
                return null
            return event
        },
    })
}
